import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { 
  MessageCircle, 
  Heart, 
  Share2, 
  Plus, 
  X, 
  Search, 
  HelpCircle, 
  BookOpen, 
  Lightbulb,
  Send,
  Trash2,
  Facebook,
  Instagram,
  ChevronLeft,
  MessageSquare,
  Twitter
} from 'lucide-react';

const CATEGORIES = [
  { id: 'all', label: 'All Posts', icon: MessageSquare },
  { id: 'question', label: 'Questions', icon: HelpCircle },
  { id: 'story', label: 'Stories', icon: BookOpen },
  { id: 'thought', label: 'Thoughts', icon: Lightbulb }
];

const getCategoryColor = (category) => {
  switch(category) {
    case 'question': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'story': return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'thought': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  }
};

const getCategoryIcon = (category) => {
  switch(category) {
    case 'question': return <HelpCircle className="w-4 h-4" />;
    case 'story': return <BookOpen className="w-4 h-4" />;
    case 'thought': return <Lightbulb className="w-4 h-4" />;
    default: return <MessageSquare className="w-4 h-4" />;
  }
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export const Forums = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMyPosts, setShowMyPosts] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // New post form state
  const [newPost, setNewPost] = useState({
    title: '',
    content: '',
    category: 'thought',
    tags: ''
  });

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await api.get(`/forums/posts?${params.toString()}`);
      setPosts(response.data);
      
      // Also fetch user's own posts (including pending)
      try {
        const myPostsResponse = await api.get('/forums/my-posts');
        setMyPosts(myPostsResponse.data);
      } catch (err) {
        // Ignore if endpoint doesn't exist
      }
    } catch (error) {
      toast.error('Failed to load forum posts');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchQuery]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPost.title.trim() || !newPost.content.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      const tags = newPost.tags.split(',').map(t => t.trim()).filter(t => t);
      await api.post('/forums/posts', {
        title: newPost.title,
        content: newPost.content,
        category: newPost.category,
        tags
      });
      toast.success('Post submitted! It will be visible after admin approval.');
      setShowCreateModal(false);
      setNewPost({ title: '', content: '', category: 'thought', tags: '' });
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const response = await api.post(`/forums/posts/${postId}/like`);
      setPosts(posts.map(p => {
        if (p.id === postId) {
          return {
            ...p,
            liked_by_user: response.data.liked,
            likes_count: response.data.liked ? p.likes_count + 1 : p.likes_count - 1
          };
        }
        return p;
      }));
      if (selectedPost?.id === postId) {
        setSelectedPost(prev => ({
          ...prev,
          liked_by_user: response.data.liked,
          likes_count: response.data.liked ? prev.likes_count + 1 : prev.likes_count - 1
        }));
      }
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await api.delete(`/forums/posts/${postId}`);
      toast.success('Post deleted');
      setPosts(posts.filter(p => p.id !== postId));
      if (selectedPost?.id === postId) setSelectedPost(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    }
  };

  const openPostDetail = async (post) => {
    setSelectedPost(post);
    setLoadingComments(true);
    try {
      const response = await api.get(`/forums/posts/${post.id}/comments`);
      setComments(response.data || []);
    } catch (error) {
      console.error('Error loading comments:', error);
      // Only show error toast if it's a real error, not just empty comments
      if (error.response?.status !== 404) {
        toast.error('Failed to load comments');
      }
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    if (!selectedPost?.id) {
      toast.error('No post selected');
      return;
    }

    try {
      setSubmitting(true);
      const response = await api.post(`/forums/posts/${selectedPost.id}/comments`, {
        content: newComment.trim()
      });
      setComments([...comments, response.data]);
      setNewComment('');
      setSelectedPost(prev => ({ ...prev, comments_count: (prev.comments_count || 0) + 1 }));
      setPosts(posts.map(p => p.id === selectedPost.id ? { ...p, comments_count: (p.comments_count || 0) + 1 } : p));
      toast.success('Comment added');
    } catch (error) {
      console.error('Error adding comment:', error);
      if (error.response?.status === 401) {
        toast.error('Please log in to add a comment');
      } else if (error.response?.status === 404) {
        toast.error('Post not found');
      } else {
        toast.error('Failed to add comment. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/forums/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId));
      setSelectedPost(prev => ({ ...prev, comments_count: prev.comments_count - 1 }));
      setPosts(posts.map(p => p.id === selectedPost.id ? { ...p, comments_count: p.comments_count - 1 } : p));
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const shareToFacebook = (post) => {
    const url = `${window.location.origin}/forums?post=${post.id}`;
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(post.title)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const shareToInstagram = (post) => {
    // Instagram doesn't have a direct web share API, so we copy the link
    const url = `${window.location.origin}/forums?post=${post.id}`;
    navigator.clipboard.writeText(`${post.title}\n\n${url}`);
    toast.success('Link copied! Paste it in your Instagram story or post');
  };

  const shareToTwitter = (post) => {
    const url = `${window.location.origin}/forums?post=${post.id}`;
    const text = `${post.title} - Check out this post on HearMeOut Forums`;
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="forums-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Mental Health Forums</h1>
          <p className="text-muted-foreground">Share your experiences, ask questions, and support others on their journey</p>
        </div>

        {/* Search and Create */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search posts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-card border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="search-forums"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowMyPosts(!showMyPosts)}
              className={`px-4 py-3 rounded-xl font-medium transition-colors ${
                showMyPosts 
                  ? 'bg-primary text-white' 
                  : 'bg-card border border-border text-foreground hover:border-primary'
              }`}
              data-testid="my-posts-toggle"
            >
              My Posts {myPosts.filter(p => p.status === 'pending').length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-yellow-500 text-white text-xs rounded-full">
                  {myPosts.filter(p => p.status === 'pending').length}
                </span>
              )}
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-medium transition-colors"
              data-testid="create-post-button"
            >
              <Plus className="w-5 h-5" />
              New Post
            </button>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                selectedCategory === cat.id
                  ? 'bg-primary text-white'
                  : 'bg-card text-muted-foreground hover:bg-muted border border-border'
              }`}
              data-testid={`category-${cat.id}`}
            >
              <cat.icon className="w-4 h-4" />
              {cat.label}
            </button>
          ))}
        </div>

        {/* Posts List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (showMyPosts ? myPosts : posts).length === 0 ? (
          <div className="text-center py-12 bg-card rounded-2xl border border-border">
            <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {showMyPosts ? 'You haven\'t posted anything yet.' : 'No posts yet. Be the first to share!'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {(showMyPosts ? myPosts : posts).map(post => (
              <div
                key={post.id}
                className={`bg-card border rounded-2xl p-6 transition-colors ${
                  post.status === 'pending' ? 'border-yellow-500/50 bg-yellow-500/5' :
                  post.status === 'declined' ? 'border-red-500/50 bg-red-500/5' :
                  'border-border hover:border-primary/50'
                }`}
                data-testid={`forum-post-${post.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(post.category)}`}>
                      {getCategoryIcon(post.category)}
                      {post.category}
                    </span>
                    {post.status === 'pending' && (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                        ⏳ Pending Approval
                      </span>
                    )}
                    {post.status === 'declined' && (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30">
                        ❌ Declined
                      </span>
                    )}
                    <span className="text-sm text-muted-foreground">{formatDate(post.created_at)}</span>
                  </div>
                  {(post.user_id === user?.user_id || user?.role === 'admin') && (
                    <button
                      onClick={() => handleDeletePost(post.id)}
                      className="text-muted-foreground hover:text-red-400 transition-colors"
                      data-testid={`delete-post-${post.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <h3 
                  className="text-lg font-semibold text-foreground mb-2 cursor-pointer hover:text-primary transition-colors"
                  onClick={() => openPostDetail(post)}
                >
                  {post.title}
                </h3>
                <p className="text-muted-foreground mb-4 line-clamp-3">{post.content}</p>

                {post.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags.map((tag, idx) => (
                      <span key={idx} className="px-2 py-1 bg-muted rounded-lg text-xs text-muted-foreground">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <span>by</span>
                    <span className="font-medium text-foreground">{post.author_name}</span>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => handleLike(post.id)}
                      className={`flex items-center gap-1.5 transition-colors ${
                        post.liked_by_user ? 'text-red-400' : 'text-muted-foreground hover:text-red-400'
                      }`}
                      data-testid={`like-post-${post.id}`}
                    >
                      <Heart className={`w-5 h-5 ${post.liked_by_user ? 'fill-current' : ''}`} />
                      <span className="text-sm">{post.likes_count}</span>
                    </button>

                    <button
                      onClick={() => openPostDetail(post)}
                      className="flex items-center gap-1.5 text-muted-foreground hover:text-primary transition-colors"
                      data-testid={`comments-post-${post.id}`}
                    >
                      <MessageCircle className="w-5 h-5" />
                      <span className="text-sm">{post.comments_count}</span>
                    </button>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => shareToTwitter(post)}
                        className="text-muted-foreground hover:text-sky-400 transition-colors"
                        title="Share to X (Twitter)"
                        data-testid={`share-twitter-${post.id}`}
                      >
                        <Twitter className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => shareToFacebook(post)}
                        className="text-muted-foreground hover:text-blue-500 transition-colors"
                        title="Share to Facebook"
                        data-testid={`share-facebook-${post.id}`}
                      >
                        <Facebook className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => shareToInstagram(post)}
                        className="text-muted-foreground hover:text-pink-500 transition-colors"
                        title="Copy link for Instagram"
                        data-testid={`share-instagram-${post.id}`}
                      >
                        <Instagram className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Post Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="create-post-modal">
          <div className="bg-card rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h2 className="text-xl font-bold text-foreground">Create New Post</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleCreatePost} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Category</label>
                <div className="flex gap-2">
                  {CATEGORIES.filter(c => c.id !== 'all').map(cat => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setNewPost({ ...newPost, category: cat.id })}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all ${
                        newPost.category === cat.id
                          ? 'bg-primary/20 border-primary text-primary'
                          : 'bg-card border-border text-muted-foreground hover:border-primary/50'
                      }`}
                    >
                      <cat.icon className="w-4 h-4" />
                      {cat.label.replace('s', '')}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Title *</label>
                <input
                  type="text"
                  value={newPost.title}
                  onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  placeholder="What's on your mind?"
                  className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                  minLength={5}
                  maxLength={200}
                  data-testid="new-post-title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Content *</label>
                <textarea
                  value={newPost.content}
                  onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                  placeholder="Share your thoughts, experiences, or questions..."
                  rows={6}
                  className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  required
                  minLength={10}
                  data-testid="new-post-content"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Tags (optional)</label>
                <input
                  type="text"
                  value={newPost.tags}
                  onChange={(e) => setNewPost({ ...newPost, tags: e.target.value })}
                  placeholder="anxiety, coping, recovery (comma separated)"
                  className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="new-post-tags"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                data-testid="submit-post-button"
              >
                {submitting ? 'Posting...' : 'Post to Forum'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Post Detail Modal */}
      {selectedPost && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="post-detail-modal">
          <div className="bg-card rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-border flex items-center gap-4">
              <button
                onClick={() => setSelectedPost(null)}
                className="text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <div className="flex-1">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(selectedPost.category)}`}>
                  {getCategoryIcon(selectedPost.category)}
                  {selectedPost.category}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => shareToTwitter(selectedPost)}
                  className="p-2 text-muted-foreground hover:text-sky-400 transition-colors"
                  title="Share to X (Twitter)"
                >
                  <Twitter className="w-5 h-5" />
                </button>
                <button
                  onClick={() => shareToFacebook(selectedPost)}
                  className="p-2 text-muted-foreground hover:text-blue-500 transition-colors"
                  title="Share to Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </button>
                <button
                  onClick={() => shareToInstagram(selectedPost)}
                  className="p-2 text-muted-foreground hover:text-pink-500 transition-colors"
                  title="Copy link for Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <h2 className="text-2xl font-bold text-foreground mb-2">{selectedPost.title}</h2>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <span>by <span className="font-medium text-foreground">{selectedPost.author_name}</span></span>
                <span>•</span>
                <span>{formatDate(selectedPost.created_at)}</span>
              </div>
              
              <p className="text-foreground whitespace-pre-wrap mb-6">{selectedPost.content}</p>

              {selectedPost.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {selectedPost.tags.map((tag, idx) => (
                    <span key={idx} className="px-3 py-1 bg-muted rounded-lg text-sm text-muted-foreground">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-4 pb-6 border-b border-border">
                <button
                  onClick={() => handleLike(selectedPost.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                    selectedPost.liked_by_user 
                      ? 'bg-red-500/20 text-red-400' 
                      : 'bg-muted text-muted-foreground hover:text-red-400'
                  }`}
                >
                  <Heart className={`w-5 h-5 ${selectedPost.liked_by_user ? 'fill-current' : ''}`} />
                  <span>{selectedPost.likes_count} likes</span>
                </button>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MessageCircle className="w-5 h-5" />
                  <span>{selectedPost.comments_count} comments</span>
                </div>
              </div>

              {/* Comments */}
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Comments</h3>
                
                {loadingComments ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                ) : comments.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No comments yet. Be the first to share your thoughts!</p>
                ) : (
                  <div className="space-y-4">
                    {comments.map(comment => (
                      <div key={comment.id} className="bg-muted rounded-xl p-4" data-testid={`comment-${comment.id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-foreground">{comment.author_name}</span>
                            <span className="text-xs text-muted-foreground">{formatDate(comment.created_at)}</span>
                          </div>
                          {(comment.user_id === user?.user_id || user?.role === 'admin') && (
                            <button
                              onClick={() => handleDeleteComment(comment.id)}
                              className="text-muted-foreground hover:text-red-400 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <p className="text-foreground">{comment.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Comment Input */}
            <div className="p-4 border-t border-border">
              <form onSubmit={handleAddComment} className="flex gap-3">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  className="flex-1 px-4 py-3 bg-muted border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="new-comment-input"
                />
                <button
                  type="submit"
                  disabled={submitting || !newComment.trim()}
                  className="px-4 py-3 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50"
                  data-testid="submit-comment-button"
                >
                  <Send className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
